import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridLayout, Layout, noCompactor, useContainerWidth } from 'react-grid-layout';
// Positioning, transitions and the drop placeholder. Its colours are
// overridden in tailwind.source.css — the stock placeholder is `red`.
import 'react-grid-layout/css/styles.css';
import { GripVertical, Plus, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    GRID_COLS,
    GRID_MARGIN,
    ROW_HEIGHT,
    WIDGET_CATEGORIES,
    WidgetCategory,
    WidgetId,
    WidgetPlacement,
    clampRows,
    nextPlacement,
    overlapRatio,
    SWAP_THRESHOLD,
    swapAndSettle,
    swapEdge,
} from './widget-layout';

/**
 * Widgets hold their ground.
 *
 * `noCompactor` on its own still lets a drag shove whatever it touches out of
 * the way, so brushing the edge of a neighbour threw it across the dashboard —
 * a gesture nobody asked for, from an overlap of a single cell. `noCompactor`
 * alone also let a resize grow straight over the widget beside it.
 *
 * `preventCollision` refuses the move instead: the dragged widget will not
 * enter an occupied cell and the occupant does not move. A widget can still be
 * dropped anywhere there is genuinely room — including space another, taller
 * widget has opened up beside it.
 *
 * Module-level so the reference is stable; a new object each render would
 * reset the grid's drag callbacks mid-gesture.
 */
const BOUNDED_LAYOUT = { ...noCompactor, preventCollision: true };

/**
 * A widget cannot be dragged below the dashboard's own depth.
 *
 * Enforced during the gesture rather than corrected after it. A correction
 * only lands if the grid reports a layout at all, and it skips
 * `onLayoutChange` whenever the drag ended where it began — which is exactly
 * what happens when a move is refused. Constraining as the pointer moves needs
 * no report, and the widget simply stops following it downward, which is the
 * clearer signal anyway.
 *
 * `constrainPosition` only — deliberately no `constrainSize`, so a widget can
 * still be stretched downward to make the dashboard taller. Growing the grid
 * is a resize; moving into space that does not exist is not.
 *
 * Per item rather than passed as the grid's `constraints` prop, because that
 * prop REPLACES the defaults, while per-item constraints run after them —
 * `gridBounds` and `minMaxSize` keep working.
 */
const ROW_BOUND = {
    name: 'rowBound',
    constrainPosition(
        item: { i: string; h: number },
        x: number,
        y: number,
        context: { layout: readonly { i: string; y: number; h: number }[] },
    ) {
        const othersReach = context.layout.reduce(
            (max, other) => (other.i === item.i ? max : Math.max(max, other.y + other.h)),
            0,
        );
        const maxY = Math.max(othersReach, item.h) - item.h;
        return { x, y: Math.max(0, Math.min(y, maxY)) };
    },
};

type SwapEdge = 'left' | 'right' | 'top' | 'bottom';

/*
 * The grid's configuration, module-level so its identity never changes.
 *
 * These feed the grid's own memos and callbacks; a fresh object on every
 * render rebuilds them, and rebuilding them mid-gesture is what a drag feels
 * as stutter.
 */
const GRID_CONFIG = {
    cols: GRID_COLS,
    rowHeight: ROW_HEIGHT,
    margin: GRID_MARGIN,
    containerPadding: [0, 0] as [number, number],
};
/** By the grip alone: widgets are full of buttons, and any press would drag. */
const DRAG_CONFIG = { handle: '[data-widget-grip]' };
const RESIZE_CONFIG = { handles: ['se', 's', 'e'] as const };

const sameLayout = (a: WidgetPlacement[], b: WidgetPlacement[]): boolean =>
    a.length === b.length &&
    a.every((p, i) => p.id === b[i].id && p.x === b[i].x && p.y === b[i].y && p.w === b[i].w && p.h === b[i].h);

/** Where the swap marker sits on the target. Literal so Tailwind can see them. */
const EDGE_CLASS: Record<'left' | 'right' | 'top' | 'bottom', string> = {
    left: 'inset-y-0 start-0 w-1',
    right: 'inset-y-0 end-0 w-1',
    top: 'inset-x-0 top-0 h-1',
    bottom: 'inset-x-0 bottom-0 h-1',
};

export interface WidgetDefinition {
    id: WidgetId;
    title: string;
    /** Shown in the picker: what this widget actually tells you. */
    description: string;
    icon: React.ReactNode;
    category: WidgetCategory;
    /** Grid units. The size it is added at; the vendor changes it from there. */
    defaultSize: { w: number; h: number };
    /**
     * How small it may be dragged. Per widget, because the floor is set by
     * content: a stat card is one number and shrinks a long way, a map stops
     * being a map. Without this a single floor has to clear the tallest
     * widget, which strands every short one above its own natural height.
     */
    minSize?: { w: number; h: number };
    render: () => React.ReactNode;
}

/**
 * The vendor's own arrangement.
 *
 * A real grid rather than a row of spans: a widget can sit anywhere, at any
 * width and any height, and two can stack in one column. Nothing compacts —
 * `noCompactor` — because a gap someone left is a decision, and a layout that
 * slides everything up to close it is arguing with them.
 *
 * Dragging is by the grip alone. Widgets are full of buttons and links, so a
 * grid that treated any press as the start of a drag would make its own
 * contents unusable.
 */
export const WidgetCanvas: React.FC<{
    layout: WidgetPlacement[];
    definitions: WidgetDefinition[];
    onChange: (next: WidgetPlacement[]) => void;
    picking: boolean;
    onPickingChange: (open: boolean) => void;
}> = ({ layout, definitions, onChange, picking, onPickingChange }) => {
    const { t } = useTranslation();
    const { containerRef, width, mounted } = useContainerWidth();

    /**
     * The same thing as state, so the target can be highlighted — but written
     * only when it CHANGES.
     *
     * The drag callback fires on every pointer frame. Setting state each time
     * re-rendered the canvas sixty times a second, and since the grid's
     * handlers were inline arrows they were rebuilt on every one of those
     * renders, which the grid re-registered mid-gesture. That was the jitter.
     */
    const [swapTarget, setSwapTarget] = useState<{ id: WidgetId; edge: SwapEdge } | null>(null);
    const swapRef = useRef<{ id: WidgetId; edge: SwapEdge } | null>(null);

    /* Read by callbacks that must keep a stable identity across renders. */
    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const byId = useMemo(() => new Map(definitions.map((d) => [d.id, d])), [definitions]);
    const placed = useMemo(() => layout.filter((p) => byId.has(p.id)), [layout, byId]);

    const gridLayout: Layout = useMemo(
        () =>
            placed.map((p) => {
                const min = byId.get(p.id)?.minSize;
                return {
                    i: p.id,
                    x: p.x,
                    y: p.y,
                    w: p.w,
                    h: p.h,
                    minW: min?.w ?? 2,
                    minH: min?.h ?? 3,
                    constraints: [ROW_BOUND],
                };
            }),
        [placed, byId],
    );

    const apply = useCallback((next: Layout) => {
        const placements = next.map((item) => ({
            id: item.i as WidgetId,
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
        }));

        const settled = clampRows(placements);

        /*
         * Nothing changed, nothing written.
         *
         * The grid reports its layout on mount as well as after a gesture.
         * Writing an equal-but-new array back would re-render, hand the grid a
         * fresh layout prop, and have it report again — a loop that never
         * settles because every pass allocates a new array.
         */
        if (sameLayout(settled, layoutRef.current)) return;
        onChangeRef.current(settled);
    }, []);

    /**
     * Which widget the dragged one is far enough over to trade places with.
     *
     * Read from the rendered boxes rather than grid coordinates, because the
     * grid refuses to move the dragged item onto an occupied cell — its grid
     * position stays where it started while the element itself follows the
     * pointer, so the element is the only thing that knows where the vendor
     * is actually pointing.
     */
    const findSwapTarget = useCallback((dragged: HTMLElement | null, draggedId: WidgetId) => {
        if (!dragged || !containerRef.current) return null;
        const from = dragged.getBoundingClientRect();

        let best: { id: WidgetId; edge: SwapEdge; ratio: number } | null = null;

        containerRef.current.querySelectorAll<HTMLElement>('[data-widget-id]').forEach((el) => {
            const id = el.dataset.widgetId as WidgetId;
            if (!id || id === draggedId) return;

            const to = el.getBoundingClientRect();
            const ratio = overlapRatio(from, to);
            if (ratio < SWAP_THRESHOLD) return;

            if (!best || ratio > best.ratio) best = { id, edge: swapEdge(from, to), ratio };
        });

        return best ? { id: best.id, edge: best.edge } : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDrag = useCallback(
        (_l: Layout, oldItem: { i: string } | null, _n: unknown, _p: unknown, _e: Event, element: HTMLElement | null) => {
            const next = oldItem ? findSwapTarget(element, oldItem.i as WidgetId) : null;
            const prev = swapRef.current;

            // Only when it actually changes — see the note on `swapTarget`.
            if (prev?.id === next?.id && prev?.edge === next?.edge) return;
            swapRef.current = next;
            setSwapTarget(next);
        },
        [findSwapTarget],
    );

    /**
     * The swap is made here, not deferred to the layout callback.
     *
     * A swap is only ever offered when the two widgets overlap by half, which
     * on the grid is always a collision — so the grid refuses the move and the
     * layout it ends the drag with is identical to the one it started with.
     * It then skips `onLayoutChange` entirely, because nothing changed. A swap
     * waiting to be applied there was never applied on drop, and went off
     * later against whatever layout the next gesture produced.
     *
     * Writing straight to `onChange` is safe: the grid re-derives its own
     * layout when the prop changes.
     */
    const handleDragStop = useCallback((_l: Layout, oldItem: { i: string } | null) => {
        const target = swapRef.current;
        swapRef.current = null;
        setSwapTarget(null);
        if (!oldItem || !target) return;

        const swapped = swapAndSettle(layoutRef.current, oldItem.i as WidgetId, target.id);
        if (!sameLayout(swapped, layoutRef.current)) onChangeRef.current(swapped);
    }, []);

    const remove = (id: WidgetId) => onChange(layout.filter((p) => p.id !== id));

    const add = (definition: WidgetDefinition) => {
        const { w, h } = definition.defaultSize;
        onChange([...layout, { id: definition.id, ...nextPlacement(layout, w, h) }]);
        onPickingChange(false);
    };

    const available = definitions.filter((d) => !layout.some((p) => p.id === d.id));

    return (
        <>
            <div ref={containerRef}>
                {mounted && placed.length > 0 && (
                    <GridLayout
                        width={width}
                        layout={gridLayout}
                        compactor={BOUNDED_LAYOUT}
                        gridConfig={GRID_CONFIG}
                        dragConfig={DRAG_CONFIG}
                        resizeConfig={RESIZE_CONFIG}
                        onLayoutChange={apply}
                        onDrag={handleDrag}
                        onDragStop={handleDragStop}
                    >
                        {placed.map((p) => {
                            const definition = byId.get(p.id)!;
                            return (
                                <div
                                    key={p.id}
                                    data-widget-id={p.id}
                                    className="group/widget relative flex min-h-0 min-w-0 flex-col"
                                >
                                    {/* One edge, on the side the dragged widget is
                                        coming from — "you are about to take this
                                        one's place", rather than an outline that
                                        would read as a selection. */}
                                    {swapTarget?.id === p.id && (
                                        <span
                                            aria-hidden
                                            className={cn(
                                                'pointer-events-none absolute z-20 rounded-full bg-ring',
                                                EDGE_CLASS[swapTarget.edge],
                                            )}
                                        />
                                    )}
                                    <div className="pointer-events-none absolute end-2 top-2 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover/widget:pointer-events-auto group-hover/widget:opacity-100">
                                        <button
                                            type="button"
                                            data-widget-grip
                                            aria-label={t('PAGES.DASHBOARD.WIDGETS.MOVE') as string}
                                            className="flex size-6 cursor-grab items-center justify-center rounded-md border bg-background text-muted-foreground active:cursor-grabbing"
                                        >
                                            <GripVertical className="size-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => remove(p.id)}
                                            aria-label={t('PAGES.DASHBOARD.WIDGETS.REMOVE') as string}
                                            className="flex size-6 items-center justify-center rounded-md border bg-background text-muted-foreground hover:text-foreground"
                                        >
                                            <X className="size-3.5" />
                                        </button>
                                    </div>

                                    {/*
                                      No `overflow-hidden` here — twice now it has
                                      eaten every widget's outline. Card draws its
                                      contour with `ring-1`, which paints OUTSIDE the
                                      box, so clipping the wrapper clips the ring.

                                      It was never needed anyway: Card carries its own
                                      `overflow-hidden`. The cell's height is the
                                      vendor's choice rather than the content's, so the
                                      card body scrolls instead of being silently cut.
                                    */}
                                    <div className="min-h-0 flex-1 [&>*]:h-full [&_[data-slot=card]]:h-full [&_[data-slot=card-content]]:min-h-0 [&_[data-slot=card-content]]:overflow-y-auto">
                                        {definition.render()}
                                    </div>
                                </div>
                            );
                        })}
                    </GridLayout>
                )}
            </div>

            {available.length > 0 && (
                <button
                    type="button"
                    onClick={() => onPickingChange(true)}
                    className={cn(
                        'mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6',
                        'text-sm text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground',
                    )}
                >
                    <Plus className="size-4" />
                    {t('PAGES.DASHBOARD.WIDGETS.ADD')}
                </button>
            )}

            <WidgetPicker open={picking} onOpenChange={onPickingChange} available={available} onAdd={add} />
        </>
    );
};

/**
 * The catalogue of what can go on the dashboard, grouped by what it reports on.
 *
 * Grouped rather than one flat list because the groups are how a vendor thinks
 * about their business — what the visualizer is doing is a different question
 * from where they stand against competitors. An empty group is not rendered: a
 * heading over nothing reads as something broken rather than something coming.
 */
export const WidgetPicker: React.FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    available: WidgetDefinition[];
    onAdd: (definition: WidgetDefinition) => void;
}> = ({ open, onOpenChange, available, onAdd }) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[80vh] max-w-[680px] overflow-y-auto sm:max-w-[680px]">
                <DialogHeader>
                    <DialogTitle>{t('PAGES.DASHBOARD.WIDGETS.PICK_TITLE')}</DialogTitle>
                    <DialogDescription>{t('PAGES.DASHBOARD.WIDGETS.PICK_BODY')}</DialogDescription>
                </DialogHeader>

                {available.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        {t('PAGES.DASHBOARD.WIDGETS.ALL_ADDED')}
                    </p>
                ) : (
                    <div className="flex flex-col gap-5">
                        {WIDGET_CATEGORIES.map((category) => {
                            const group = available.filter((d) => d.category === category);
                            if (!group.length) return null;

                            return (
                                <section key={category}>
                                    <h3 className="mb-2 text-xs font-medium text-muted-foreground">
                                        {t(`PAGES.DASHBOARD.WIDGETS.CATEGORY.${category.toUpperCase()}`)}
                                    </h3>
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        {group.map((definition) => (
                                            <button
                                                key={definition.id}
                                                type="button"
                                                onClick={() => onAdd(definition)}
                                                className="flex items-start gap-3 rounded-xl border p-3 text-start transition-colors hover:bg-accent/40"
                                            >
                                                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg]:size-4">
                                                    {definition.icon}
                                                </span>
                                                <span className="flex min-w-0 flex-col gap-0.5">
                                                    <span className="text-sm font-medium">{definition.title}</span>
                                                    <span className="text-xs leading-relaxed text-muted-foreground">
                                                        {definition.description}
                                                    </span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

/** The `+` beside the section heading — the other way into the picker. */
export const AddWidgetButton: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
    const { t } = useTranslation();
    return (
        <Button variant="outline" size="icon-sm" aria-label={t('PAGES.DASHBOARD.WIDGETS.ADD') as string} onClick={onOpen}>
            <Plus />
        </Button>
    );
};
